<template>
  <div :class="`propeller-grid-toolbar ${className || ''}`" :data-view-mode="currentViewMode">
    <div class="propeller-grid-toolbar__bar flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
      <div class="propeller-grid-toolbar__count text-sm text-muted-foreground font-medium">
        <template v-if="itemsFound !== undefined && itemsFound > 0">
          <span>{{ itemsFound }} {{ itemsFound === 1 ? getLabel('productSingular') : getLabel('productPlural') }}</span>
        </template>
      </div>
      <div class="propeller-grid-toolbar__controls flex flex-wrap items-center gap-3">
        <select
          class="propeller-grid-toolbar__select propeller-grid-toolbar__select--offset h-9 rounded-[var(--radius-control)] border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          :value="currentOffset"
          @change="async (e) => handleOffsetChange(parseInt((e.target as HTMLSelectElement).value))"
        >
          <template :key="n" v-for="(n, index) in getOffsetOptions()">
            <option :value="n">{{ n }}{{ getLabel('perPage') }}</option>
          </template>
        </select>
        <div class="propeller-grid-toolbar__divider h-4 w-px bg-border hidden sm:block"></div>
        <select
          class="propeller-grid-toolbar__select propeller-grid-toolbar__select--sort-field h-9 rounded-[var(--radius-control)] border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          :value="currentSortField"
          @change="async (e) => handleSortFieldChange((e.target as HTMLSelectElement).value)"
        >
          <template :key="field" v-for="(field, index) in getSortOptions()">
            <option :value="field" :disabled="field === 'PRICE' && isPriceSortDisabled()">
              {{ getLabel(field) }}
            </option>
          </template></select
        ><select
          class="propeller-grid-toolbar__select propeller-grid-toolbar__select--sort-order h-9 rounded-[var(--radius-control)] border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          :value="currentSortOrder"
          @change="async (e) => handleSortOrderChange((e.target as HTMLSelectElement).value)"
        >
          <option :value="SortOrder.ASC">{{ getLabel('ASC') }}</option>
          <option :value="SortOrder.DESC">
            {{ getLabel('DESC') }}
          </option></select
        ><button
          type="button"
          class="propeller-grid-toolbar__view-toggle h-9 w-9 flex items-center justify-center rounded-[var(--radius-control)] border border-input bg-transparent hover:bg-accent hover:text-accent-foreground transition-colors"
          @click="async (event) => handleViewChange()"
          :title="currentViewMode === 'grid' ? getLabel('switchToList') : getLabel('switchToGrid')"
        >
          <template v-if="currentViewMode === 'grid'">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
              <line x1="3" y1="6" x2="3.01" y2="6"></line>
              <line x1="3" y1="12" x2="3.01" y2="12"></line>
              <line x1="3" y1="18" x2="3.01" y2="18"></line>
            </svg>
          </template>

          <template v-if="currentViewMode === 'list'">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          </template>
        </button>
      </div>
    </div>
    <template v-if="hasActiveFilters()">
      <div class="propeller-grid-toolbar__active-filters flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          class="propeller-grid-toolbar__clear-all h-7 px-2 text-xs rounded-[var(--radius-control)] hover:bg-accent hover:text-accent-foreground transition-colors"
          @click="
            async (event) => {
              if (onClearFilters) onClearFilters();
            }
          "
        >
          {{ getLabel('clearAll') }}
        </button>
        <template v-if="priceFilterMin !== undefined || priceFilterMax !== undefined">
          <span
            class="propeller-grid-toolbar__filter-badge propeller-grid-toolbar__filter-badge--price inline-flex items-center gap-1 cursor-pointer px-2.5 py-0.5 rounded-full text-xs font-semibold border border-input bg-background hover:bg-primary hover:text-destructive-foreground hover:border-primary transition-colors"
            @click="
              async (event) => {
                if (onPriceFilterRemove) onPriceFilterRemove();
              }
            "
            >{{ getLabel('price') }}: {{ currencySymbol }} {{ priceFilterMin ?? 0 }} – {{ currencySymbol }}{{ priceFilterMax ?? '∞'
            }}<span class="propeller-grid-toolbar__filter-badge-remove">×</span></span
          >
        </template>

        <template v-if="availability === 'in-stock'">
          <span
            class="propeller-grid-toolbar__filter-badge propeller-grid-toolbar__filter-badge--availability inline-flex items-center gap-1 cursor-pointer px-2.5 py-0.5 rounded-full text-xs font-semibold border border-input bg-background hover:bg-primary hover:text-destructive-foreground hover:border-primary transition-colors"
            @click="
              async (event) => {
                if (onAvailabilityFilterRemove) onAvailabilityFilterRemove();
              }
            "
            >{{ getAvailabilityLabel() }}<span class="propeller-grid-toolbar__filter-badge-remove">×</span></span
          >
        </template>

        <template
          :key="`${badge.key}-${badge.value}`"
          v-for="(badge, index) in getActiveFilterBadges()"
        >
          <span
            class="propeller-grid-toolbar__filter-badge inline-flex items-center gap-1 cursor-pointer px-2.5 py-0.5 rounded-full text-xs font-semibold border border-input bg-background hover:bg-primary hover:text-destructive-foreground hover:border-primary transition-colors"
            :data-filter-key="badge.key"
            @click="
              async (event) => {
                if (onFilterRemove) onFilterRemove(badge.key, badge.value);
              }
            "
            >{{ badge.value }}<span class="propeller-grid-toolbar__filter-badge-remove">×</span></span
          >
        </template>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';

import { Contact, Customer, ProductSortField, SortOrder } from '@propeller-commerce/propeller-sdk-v2';
import { type Availability, MIN_STOCK_THRESHOLD } from '@propeller-commerce/propeller-v2-core-ui';
import { useInfraProps } from '../composables/vue/useInfraProps';
// Default sort field keys shown in the dropdown when sortOptions is not provided.
const ALL_SORT_FIELDS: string[] = [
  ProductSortField.CATEGORY_ORDER,
  ProductSortField.NAME,
  ProductSortField.PRICE,
  ProductSortField.SKU,
  ProductSortField.SUPPLIER_CODE,
  ProductSortField.CREATED_AT,
  ProductSortField.LAST_MODIFIED_AT,
  ProductSortField.RELEVANCE,
  ProductSortField.PRIORITY,
];

// Built-in label defaults (can be overridden via the labels prop).
// Built-in label defaults (can be overridden via the labels prop).
const DEFAULT_LABELS: Record<string, string> = {
  [ProductSortField.CATEGORY_ORDER]: 'Default Sorting',
  [ProductSortField.NAME]: 'Name',
  [ProductSortField.PRICE]: 'Price',
  [ProductSortField.SKU]: 'SKU',
  [ProductSortField.SUPPLIER_CODE]: 'Supplier Code',
  [ProductSortField.CREATED_AT]: 'Created Date',
  [ProductSortField.LAST_MODIFIED_AT]: 'Last Modified Date',
  [ProductSortField.RELEVANCE]: 'Relevance',
  [ProductSortField.PRIORITY]: 'Priority',
  [SortOrder.ASC]: 'Low to High',
  [SortOrder.DESC]: 'High to Low',
  clearAll: 'Clear All',
  products: ' Products',
  productSingular: 'Product',
  productPlural: 'Products',
  from: 'from',
  results: 'results',
  perPage: ' per page',
  price: 'Price',
  inStock: 'In stock',
  switchToList: 'Switch to list view',
  switchToGrid: 'Switch to grid view',
};

// Default sort field keys shown in the dropdown when sortOptions is not provided.

export interface GridToolbarProps {
  /**
   * Sort field keys to show in the sort dropdown.
   * Accepts keys of the ProductSortField enum (e.g. 'NAME', 'PRICE').
   * Defaults to all available sort fields.
   */
  sortOptions?: string[];

  /**
   * Hide the price-ascending/descending sort options entirely. Default: false.
   * Useful for closed B2B portals where prices are "by quotation".
   */
  hidePriceSort?: boolean;

  /**
   * Active sort — first element is used.
   * Defaults to [{ field: 'CATEGORY_ORDER', order: 'DESC' }].
   */
  defaultSort?: {
    field: string;
    order: string;
  }[];

  /**
   * Layout mode: 'grid' or 'list'.
   * Controls which icon the view-toggle button shows.
   * Defaults to 'grid'.
   */
  viewMode?: 'grid' | 'list';

  /**
   * Available page-size options shown in the per-page dropdown.
   * Defaults to [12, 24, 48].
   */
  offset?: number[];

  /**
   * Initially selected page size.
   * Defaults to 12.
   */
  defaultOffset?: number;

  /**
   * Called when the sort field or sort direction changes.
   * Receives the new field key and direction ('ASC'|'DESC').
   */
  onSortChange?: (field: string, order: string) => void;

  /**
   * Called when the user selects a different per-page value.
   * Receives the new page size number.
   */
  onOffsetChange?: (offset: number) => void;

  /**
   * Called when the user clicks the view-mode toggle button.
   * Receives the new mode: 'grid' or 'list'.
   */
  onViewChange?: (mode: 'grid' | 'list') => void;

  /**
   * Total products found — displayed as a result count on the left side.
   * Pass 0 or undefined to hide the count.
   */
  itemsFound?: number;

  /**
   * Current page number. Used together with `pageSize` and `itemsFound`
   * to display a range indicator (e.g. "1–10 from 594 results").
   * When omitted the component falls back to a simple total count.
   */
  page?: number;

  /**
   * Items per page. Used together with `page` and `itemsFound`
   * to compute the result range. Defaults to 12.
   */
  pageSize?: number;

  /**
   * Actual number of items visible on the current page.
   * When provided, overrides `pageSize` for the range end calculation.
   */
  pageItemCount?: number;

  /**
   * Currently active attribute filter selections.
   * Key = attribute name, value = array of selected values.
   * Used to render removable filter badges.
   */
  activeTextFilters?: Record<string, string[]>;

  /**
   * Currently active price filter lower bound.
   * When defined (together with or without priceFilterMax), renders a price badge.
   */
  priceFilterMin?: number;

  /**
   * Currently active price filter upper bound.
   */
  priceFilterMax?: number;

  /**
   * Called when an attribute filter badge × is clicked.
   * Receives the attribute name and the specific value to remove.
   */
  onFilterRemove?: (filterName: string, value: string) => void;

  /**
   * Called when the price filter badge × is clicked.
   */
  onPriceFilterRemove?: () => void;

  /**
   * Currently active availability (stock) selection.
   * Renders a single removable chip when set to `'in-stock'`.
   */
  availability?: Availability;

  /** Currently active minimum stock quantity, shown on the chip above the default. */
  minStock?: number;

  /** Called when the availability filter badge × is clicked. */
  onAvailabilityFilterRemove?: () => void;

  /**
   * Called when "Clear All" is clicked.
   */
  onClearFilters?: () => void;

  /**
   * Label overrides. Supply any subset of DEFAULT_LABELS keys plus
   * any of the ProductSortField key strings to customise display text.
   */
  labels?: Record<string, string>;

  /**
   * Portal visibility mode.
   * 'open'        — price sorting is available for all users.
   * 'semi-closed' — price sorting is disabled for unauthenticated users.
   */
  portalMode?: string;

  /**
   * Authenticated user object.
   * When null/undefined in semi-closed mode the PRICE sort option is disabled.
   */
  user?: Contact | Customer | null;

  /** Extra CSS class applied to the root element. */
  className?: string;
}

/** Flat badge item used when rendering the active-filters bar. */
// Default sort field keys shown in the dropdown when sortOptions is not provided.

/** Flat badge item used when rendering the active-filters bar. */
interface FilterBadge {
  key: string;
  value: string;
}
// Default sort field keys shown in the dropdown when sortOptions is not provided.

/** Flat badge item used when rendering the active-filters bar. */

interface GridToolbarState {
  currentSortField: string;
  currentSortOrder: string;
  currentOffset: number;
  currentViewMode: 'grid' | 'list';
  getLabel: (key: string) => string;
  getSortOptions: () => string[];
  getOffsetOptions: () => number[];
  hasActiveFilters: () => boolean;
  getActiveFilterBadges: () => FilterBadge[];
  isPriceSortDisabled: () => boolean;
  getAvailabilityLabel: () => string;
  handleSortFieldChange: (field: string) => void;
  handleSortOrderChange: (order: string) => void;
  handleOffsetChange: (offset: number) => void;
  handleViewChange: () => void;
}

const props = defineProps<GridToolbarProps>();

// The active price-filter chip rendered its euro inline as text — no prop and
// no class, so a non-euro shop had no way to reach it at all.
const infra = useInfraProps(props);
const currencySymbol = computed(() => (infra.currency as string | undefined) ?? '€');
const currentSortField = ref<GridToolbarState['currentSortField']>(
  ProductSortField.CATEGORY_ORDER
);
const currentSortOrder = ref<GridToolbarState['currentSortOrder']>(SortOrder.DESC);
const currentOffset = ref<GridToolbarState['currentOffset']>(12);
const currentViewMode = ref<GridToolbarState['currentViewMode']>('grid');

watch(
  () => [props.defaultSort],
  () => {
    const sort =
      (props.defaultSort as {
        field: string;
        order: string;
      }[]) || [];
    currentSortField.value =
      sort.length > 0
        ? sort[0].field || ProductSortField.CATEGORY_ORDER
        : ProductSortField.CATEGORY_ORDER;
    currentSortOrder.value =
      sort.length > 0 ? sort[0].order || SortOrder.DESC : SortOrder.DESC;
  },
  { immediate: true }
);
watch(
  () => [props.defaultOffset],
  () => {
    currentOffset.value = (props.defaultOffset as number) || 12;
  },
  { immediate: true }
);
watch(
  () => [props.viewMode],
  () => {
    if (props.viewMode) {
      currentViewMode.value = props.viewMode;
    }
  },
  { immediate: true }
);
function getLabel(key: string): ReturnType<GridToolbarState['getLabel']> {
  const labels = (props.labels as Record<string, string>) || {};
  return labels[key] !== undefined ? labels[key] : DEFAULT_LABELS[key] || key;
}
function getSortOptions(): ReturnType<GridToolbarState['getSortOptions']> {
  const opts = (props.sortOptions as string[]) || [];
  const base = opts.length > 0 ? opts : ALL_SORT_FIELDS;
  return props.hidePriceSort
    ? base.filter((f) => f !== ProductSortField.PRICE)
    : base;
}
function getOffsetOptions(): ReturnType<GridToolbarState['getOffsetOptions']> {
  const opts = (props.offset as number[]) || [];
  return opts.length > 0 ? opts : [12, 24, 48];
}
function hasActiveFilters(): ReturnType<GridToolbarState['hasActiveFilters']> {
  const text = (props.activeTextFilters as Record<string, string[]>) || {};
  const hasText = Object.keys(text).some((k) => (text[k] || []).length > 0);
  const hasPrice = props.priceFilterMin !== undefined || props.priceFilterMax !== undefined;
  const hasAvailability = props.availability === 'in-stock';
  return hasText || hasPrice || hasAvailability;
}
function getActiveFilterBadges(): ReturnType<GridToolbarState['getActiveFilterBadges']> {
  const text = (props.activeTextFilters as Record<string, string[]>) || {};
  const badges: FilterBadge[] = [];
  Object.entries(text)
    .filter(([, values]) => (values || []).length > 0)
    .forEach(([key, values]) => {
      (values || []).forEach((value: string) => {
        badges.push({
          key,
          value,
        });
      });
    });
  return badges;
}
function isPriceSortDisabled(): ReturnType<GridToolbarState['isPriceSortDisabled']> {
  return (props.portalMode as string) === 'semi-closed' && !props.user;
}
function getAvailabilityLabel(): ReturnType<GridToolbarState['getAvailabilityLabel']> {
  const qty = props.minStock as number | undefined;
  if (qty !== undefined && qty > MIN_STOCK_THRESHOLD) return `${getLabel('inStock')}: ${qty}+`;
  return getLabel('inStock');
}
function handleSortFieldChange(
  field: string
): ReturnType<GridToolbarState['handleSortFieldChange']> {
  currentSortField.value = field;
  if (props.onSortChange) props.onSortChange(field, currentSortOrder.value);
}
function handleSortOrderChange(
  order: string
): ReturnType<GridToolbarState['handleSortOrderChange']> {
  currentSortOrder.value = order;
  if (props.onSortChange) props.onSortChange(currentSortField.value, order);
}
function handleOffsetChange(offset: number): ReturnType<GridToolbarState['handleOffsetChange']> {
  currentOffset.value = offset;
  if (props.onOffsetChange) props.onOffsetChange(offset);
}
function handleViewChange(): ReturnType<GridToolbarState['handleViewChange']> {
  const next = currentViewMode.value === 'grid' ? 'list' : 'grid';
  currentViewMode.value = next;
  if (props.onViewChange) props.onViewChange(next);
}
</script>
